import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, Building2, Palette, TrendingUp, Package, ShoppingBag, Star, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { adminAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [dashboardData, setDashboardData] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingDesigns, setPendingDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionModal, setActionModal] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [dashRes, usersRes, designsRes] = await Promise.all([
        adminAPI.getDashboard(),
        adminAPI.getUsers(),
        adminAPI.getPendingDesigns()
      ]);
      setDashboardData(dashRes.data);
      setUsers(usersRes.data);
      setPendingDesigns(designsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await adminAPI.approveUser(userId);
      toast({
        title: "User Approved",
        description: "User has been activated successfully.",
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Action Failed",
        description: error.response?.data?.detail || "Failed to approve user.",
        variant: "destructive"
      });
    }
  };

  const handleRejectUser = async (userId, reason) => {
    try {
      await adminAPI.rejectUser(userId, reason);
      toast({
        title: "User Rejected",
        description: "User application has been rejected.",
      });
      fetchData();
      setActionModal(null);
    } catch (error) {
      toast({
        title: "Action Failed",
        description: error.response?.data?.detail || "Failed to reject user.",
        variant: "destructive"
      });
    }
  };

  const handleApproveDesign = async (designId, notes) => {
    try {
      await adminAPI.approveDesign(designId, notes);
      toast({
        title: "Design Approved",
        description: "Design has been approved successfully.",
      });
      fetchData();
      setActionModal(null);
    } catch (error) {
      toast({
        title: "Action Failed",
        description: error.response?.data?.detail || "Failed to approve design.",
        variant: "destructive"
      });
    }
  };

  const handleRejectDesign = async (designId, reason) => {
    try {
      await adminAPI.rejectDesign(designId, reason);
      toast({
        title: "Design Rejected",
        description: "Design has been rejected.",
      });
      fetchData();
      setActionModal(null);
    } catch (error) {
      toast({
        title: "Action Failed",
        description: error.response?.data?.detail || "Failed to reject design.",
        variant: "destructive"
      });
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <Shield className="h-24 w-24 text-gray-300 mx-auto mb-6" />
          <h1 className="text-3xl font-bold mb-4">Admin Access Required</h1>
          <p className="text-gray-600 mb-8">You don't have permission to access this portal.</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-bold">Admin Dashboard</h1>
            <p className="text-gray-600">Manage platform users, products, and approvals</p>
          </div>
        </div>

        {/* Dashboard Stats */}
        {dashboardData && (
          <>
            <div className="grid md:grid-cols-5 gap-6 mb-8">
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Consumers</p>
                    <p className="text-3xl font-bold">{dashboardData.users.consumers}</p>
                  </div>
                  <Users className="h-10 w-10 text-blue-600" />
                </div>
              </Card>
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Institutional</p>
                    <p className="text-3xl font-bold">{dashboardData.users.institutional}</p>
                  </div>
                  <Building2 className="h-10 w-10 text-blue-600" />
                </div>
              </Card>
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Designers</p>
                    <p className="text-3xl font-bold">{dashboardData.users.designers}</p>
                  </div>
                  <Palette className="h-10 w-10 text-purple-600" />
                </div>
              </Card>
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Investors</p>
                    <p className="text-3xl font-bold">{dashboardData.users.investors}</p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-green-600" />
                </div>
              </Card>
              <Card className="p-6 bg-orange-50 border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Pending</p>
                    <p className="text-3xl font-bold text-orange-700">{dashboardData.pending_approvals.total}</p>
                  </div>
                  <Clock className="h-10 w-10 text-orange-600" />
                </div>
              </Card>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Total Orders</p>
                    <p className="text-3xl font-bold">{dashboardData.platform.orders}</p>
                  </div>
                  <ShoppingBag className="h-10 w-10 text-red-600" />
                </div>
              </Card>
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Total Products</p>
                    <p className="text-3xl font-bold">{dashboardData.platform.products}</p>
                  </div>
                  <Package className="h-10 w-10 text-red-600" />
                </div>
              </Card>
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Total Reviews</p>
                    <p className="text-3xl font-bold">{dashboardData.platform.reviews}</p>
                  </div>
                  <Star className="h-10 w-10 text-yellow-600" />
                </div>
              </Card>
            </div>
          </>
        )}

        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList>
            <TabsTrigger value="pending">Pending Approvals</TabsTrigger>
            <TabsTrigger value="users">All Users</TabsTrigger>
            <TabsTrigger value="designs">Designs</TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <div className="space-y-6">
              {/* Pending Users */}
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-4">Pending User Approvals</h2>
                {users.filter(u => u.status === 'pending').length === 0 ? (
                  <p className="text-gray-600 text-center py-8">No pending user approvals</p>
                ) : (
                  <div className="space-y-4">
                    {users.filter(u => u.status === 'pending').map((user) => (
                      <div key={user.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-bold text-lg">{user.name}</h3>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                user.role === 'institutional' ? 'bg-blue-100 text-blue-700' :
                                user.role === 'designer' ? 'bg-purple-100 text-purple-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                              </span>
                            </div>
                            <p className="text-gray-600 text-sm mb-2">{user.email}</p>
                            {user.company_name && (
                              <div className="text-sm space-y-1 mt-3 bg-gray-50 p-3 rounded">
                                <p><strong>Company:</strong> {user.company_name}</p>
                                <p><strong>GST:</strong> {user.gst_number}</p>
                                <p><strong>Address:</strong> {user.registered_address}</p>
                              </div>
                            )}
                            {user.portfolio_url && (
                              <p className="text-sm mt-2"><strong>Portfolio:</strong> <a href={user.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-blue-600">{user.portfolio_url}</a></p>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleApproveUser(user.id)}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => setActionModal({ type: 'reject-user', data: user })}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Pending Designs */}
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-4">Pending Design Approvals</h2>
                {pendingDesigns.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">No pending design approvals</p>
                ) : (
                  <div className="space-y-4">
                    {pendingDesigns.map((design) => (
                      <div key={design.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-bold text-lg mb-2">{design.design_name}</h3>
                            <p className="text-gray-600 text-sm mb-3">{design.description}</p>
                            <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                              <div>
                                <p className="text-gray-500">Category</p>
                                <p className="font-semibold">{design.category}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Est. Cost</p>
                                <p className="font-semibold">₹{design.estimated_cost}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Target Price</p>
                                <p className="font-semibold">₹{design.target_price}</p>
                              </div>
                            </div>
                            {design.images && design.images.length > 0 && (
                              <div className="flex gap-2 mb-3">
                                {design.images.slice(0, 3).map((img, idx) => (
                                  <img key={idx} src={img} alt="" className="w-20 h-20 object-cover rounded" />
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => setActionModal({ type: 'approve-design', data: design })}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => setActionModal({ type: 'reject-design', data: design })}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="users">
            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">All Users</h2>
              <div className="space-y-3">
                {users.map((user) => (
                  <div key={user.id} className="border rounded p-3 flex justify-between items-center">
                    <div>
                      <p className="font-semibold">{user.name}</p>
                      <p className="text-sm text-gray-600">{user.email}</p>
                    </div>
                    <div className="flex gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        user.role === 'consumer' ? 'bg-blue-100 text-blue-700' :
                        user.role === 'institutional' ? 'bg-blue-100 text-blue-700' :
                        user.role === 'designer' ? 'bg-purple-100 text-purple-700' :
                        user.role === 'investor' ? 'bg-green-100 text-green-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {user.role}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        user.status === 'active' ? 'bg-green-100 text-green-700' :
                        user.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {user.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="designs">
            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">All Design Submissions</h2>
              <p className="text-gray-600 text-center py-8">Design management coming soon</p>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Action Modals */}
      {actionModal && (
        <ActionModal
          type={actionModal.type}
          data={actionModal.data}
          onConfirm={(reason) => {
            if (actionModal.type === 'reject-user') {
              handleRejectUser(actionModal.data.id, reason);
            } else if (actionModal.type === 'approve-design') {
              handleApproveDesign(actionModal.data.id, reason);
            } else if (actionModal.type === 'reject-design') {
              handleRejectDesign(actionModal.data.id, reason);
            }
          }}
          onClose={() => setActionModal(null)}
        />
      )}

      <Footer />
    </div>
  );
};

const ActionModal = ({ type, data, onConfirm, onClose }) => {
  const [reason, setReason] = useState('');

  const titles = {
    'reject-user': 'Reject User Application',
    'approve-design': 'Approve Design',
    'reject-design': 'Reject Design'
  };

  const labels = {
    'reject-user': 'Rejection Reason',
    'approve-design': 'Approval Notes (Optional)',
    'reject-design': 'Rejection Reason'
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full p-6">
        <h2 className="text-xl font-bold mb-4">{titles[type]}</h2>
        <p className="text-gray-600 mb-4">
          {data.name || data.design_name}
        </p>
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">{labels[type]}</label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Enter your notes..."
            required={type.includes('reject')}
          />
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(reason)}
            className="flex-1"
            disabled={type.includes('reject') && !reason}
          >
            Confirm
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AdminDashboard;
